import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, MapPinned, EyeOff } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { US_STATES } from "../lib/constants";
import { StatesPicker, alertSummary, AlertStatesPicker } from "./AlertStatesPicker";
import { AddSignInForm, PinControls, RemoveSignIn, ContactControls } from "./SignInControls";

const ROLE_LABEL = { admin: "Admin", member: "Member", demo: "Demo" };

const LimitedButton = ({ u, reload }) => (
  <button data-testid={`login-user-limited-${u.id}`} title="View only: sees just the Potential and Signed deals you give them"
    onClick={async () => {
      try {
        await api.put(`/users/${u.id}/limited`, { limited: !u.limited });
        toast.success(u.limited ? `${u.name} has full rep access again` : `${u.name} is now view only: just Potential and Signed deals you give them`);
        reload();
      } catch (e) { toast.error(formatApiError(e)); }
    }}
    className={`h-9 px-3 rounded-full border text-xs font-semibold flex items-center gap-1.5 transition-colors ${u.limited ? "bg-amber-100 border-amber-300 text-amber-900" : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"}`}>
    <EyeOff className="h-3.5 w-3.5" />{u.limited ? "View only: On" : "View only: Off"}
  </button>
);

const StatesButton = ({ u, reload }) => (
  <StatesPicker value={u.allowed_states} valueNone={u.no_states} testid="user-states-popover" title={`States ${u.name} can see`}
    note="Leads, map, counts, sharing and scans are limited to these states." emptyNote="All states = sees every state"
    noneLabel="No states" noneNote="No states = sees nothing until you assign a territory (you can still put leads in their lists)"
    onSave={async (states, none) => {
      try {
        const { data } = await api.put(`/users/${u.id}/states`, { states, none });
        const saved = data.allowed_states;
        toast.success(data.no_states ? `${u.name} has no states: sees nothing until you assign a territory` : `${u.name} can now see ${saved.length ? saved.map((s) => US_STATES[s]).join(", ") : "all states"}`);
        reload();
      } catch (err) { toast.error(formatApiError(err)); }
    }}
    trigger={
      <button className="h-9 px-4 rounded-full border border-slate-200 text-sm font-medium text-blue-700 hover:border-blue-400 transition-colors flex items-center gap-1.5 shrink-0" data-testid={`login-user-edit-${u.id}`}>
        <MapPinned className="h-4 w-4" />Change states
      </button>
    } />
);

const UserRow = ({ u, reload }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid={`login-user-${u.id}`}>
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="font-semibold text-slate-900 truncate">{u.name}
          <span className="ml-2 text-[10px] font-mono-label px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{ROLE_LABEL[u.role] || u.role}</span>
        </p>
        <p className="text-xs text-slate-500 mt-0.5" data-testid={`login-user-states-${u.id}`}>
          {u.role === "demo" ? `Deep dive only in: ${u.allowed_states?.length ? alertSummary(u.allowed_states) : "no state yet (blocked)"}` : u.role === "admin" ? "All states" : u.no_states ? "No states: sees nothing until assigned" : `Can see: ${alertSummary(u.allowed_states)}`}
        </p>
      </div>
      {u.role !== "admin" && <RemoveSignIn u={u} reload={reload} />}
    </div>
    {u.role === "member" && <ContactControls u={u} reload={reload} />}
    <div className="flex flex-wrap items-center gap-2">
      <PinControls u={u} reload={reload} />
      {["member", "demo"].includes(u.role) && <StatesButton u={u} reload={reload} />}
      {u.role === "member" && <LimitedButton u={u} reload={reload} />}
      {u.contact && <span className="text-sm text-slate-600 flex items-center gap-1.5" data-testid={`login-user-alerts-${u.id}`}><AlertStatesPicker member={u.contact} onSaved={reload} /></span>}
    </div>
  </div>
);

export const UserAccessPanel = ({ onTeamChange }) => {
  const [users, setUsers] = useState([]);
  const load = useCallback(() => {
    onTeamChange?.();
    return api.get("/users").then((r) => setUsers(r.data)).catch((e) => console.warn("users", e));
  }, [onTeamChange]);
  useEffect(() => { load(); }, [load]);

  return (
    <section className="space-y-3" data-testid="user-access-panel">
      <div>
        <h2 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2"><KeyRound className="h-5 w-5 text-blue-700" />Team &amp; sign-ins</h2>
        <p className="text-sm text-slate-500">Everyone who uses the app: PIN, email, cell, states they can see and alert states. Lead emails go to the email shown here.</p>
      </div>
      <AddSignInForm onAdded={load} />
      <div className="grid md:grid-cols-2 gap-3">
        {users.map((u) => <UserRow key={u.id} u={u} reload={load} />)}
      </div>
    </section>
  );
};
